import { Space, Tooltip } from 'antd';
import moment from 'moment';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { getManifestActiveTabKey } from './utils';
import { ManifestTabKey, ManifestTabKeys } from './types';
import NewJobButtonPopover from './components/NewJobButtonPopover';
import ManifestActionsPopover from './components/ManifestActionsPopover';
import CreateMultipleEnsModal from './components/CreateMultipleEnsModal';
import AddJobModal from '../components/AddJobModal';
import Button from 'components/new-ui/base/button/Button';
import useManifests from 'hooks/useManifests';
import ManifestStatus from '../components/manifest-status/ManifestStatus';
import type { ManifestDeclarationsResponse, UpdateManifestExternalReferencesPayload } from 'store/manifests/types';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { GetJobsParams, JobResponse, JobStatuses } from 'store/jobs/job';
import { listJobs } from 'store/jobs/client';
import { debounce } from 'lodash';
import { DefaultOptionType } from 'antd/lib/select';
import { AxiosError } from 'axios';
import { ManifestStatuses, type ManifestStatus as ManifestStatusType } from '../types/manifest-statuses';
import { hasPermissions } from 'config/roles/hasPermissions';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import useSession from 'hooks/useSession';
import { getErrorMessage } from 'utils/getErrorMessage';
import EditManifestFormModal from '../components/update-manifest-form/EditManifestFormModal';
import ExternalReferencesModal from '../components/ExternalReferencesModal';
import AddThirdPartyJobModal from '../components/third-party-job/AddThirdPartyJobModal';
import { ExternalReference, ReferenceTypes, getManifestExternalReferences } from 'utils/external-references';
import { Events } from 'utils/events.enum';
import { getManifestDeclarations, getManifestJobsWithTransportDetailsDiscrepancies } from 'store/manifests/client';
import NonAmendableDeclarationsModal from './components/non-amendable-declarations/NonAmendableDeclarationsModal';
import {
    AMENDABLE_DECLARATION_STATUSES,
    EDITABLE_DECLARATION_STATUSES,
    getJobsWithNonAmendableDeclarations,
} from 'store/declarations/utils';
import TransportDetailsDiscrepenciesWarning from './components/TransportDetailsDiscrepenciesWarning';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';

type ActiveModal =
    | 'add-existing-job'
    | 'add-external-reference'
    | 'edit-manifest'
    | 'add-third-party-job'
    | 'non-amendable-declarations'
    | 'create-multiple-ens';

type DeclarationsState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'disabled'; tooltip: string }
    | { status: 'ready'; hasAmendableDeclarations: boolean; nonAmendableJobs: ManifestDeclarationsResponse[] };

const defaultListJobsParams: GetJobsParams = {
    size: 100,
    page: 0,
    jobStatus: [JobStatuses.NEW],
    assignedToManifest: false,
};

interface Props {
    children: ReactNode;
}

const ManifestDetails = ({ children }: Props) => {
    const navigate = useNavigate();
    const { userInfo } = useSession();
    const location = useLocation();
    const { manifestId } = useParams<{ manifestId: string }>();
    const { manifest, getManifestById, addJobToManifest, updateManifestExternalReferences } = useManifests();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [activeTabKey, setActiveTabKey] = useState<ManifestTabKey>(getManifestActiveTabKey(location));
    const [showNewJobPopup, setShowNewJobPopup] = useState(false);
    const [showActionsPopover, setShowActionsPopover] = useState(false);
    const [isLoadingManifest, setIsLoadingManifest] = useState(false);
    const [jobsResults, setJobsResults] = useState<JobResponse[]>([]);
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
    const [jobsWithDiscrepancies, setJobsWithDiscrepacies] = useState<ManifestDeclarationsResponse[]>([]);
    const [isLoadingDiscrepancies, setIsLoadingDiscrepancies] = useState(false);
    const [declarationsState, setDeclarationsState] = useState<DeclarationsState>({ status: 'loading' });

    const formattedCreatedAt = moment(manifest?.createdAt).format('DD/MM/YYYY');
    const canCreateJobs = hasPermissions(userInfo, 'jobs', 'CREATE');
    const canUpdateManifests = hasPermissions(userInfo, 'manifests', 'UPDATE');
    const isCompleted = manifest?.status === ManifestStatuses.COMPLETED;
    const isDraft = manifest?.status === ManifestStatuses.DRAFT;
    const canUpdateManifestExternalReferences = hasPermissions(userInfo, 'manifests', 'UPDATE_EXTERNAL_REFERENCES');
    const canCreateDeclarations = hasPermissions(userInfo, 'declarations', 'CREATE');
    const isAccompanied = Boolean(manifest?.declarationsData?.containerIndicator);
    const editManifestTooltip =
        declarationsState.status === 'disabled'
            ? declarationsState.tooltip
            : declarationsState.status === 'error'
            ? 'Could not load declaration status. Please reload the page.'
            : undefined;

    const openModal = (modal: ActiveModal) => setActiveModal(modal);
    const isModalOpen = (modal: ActiveModal) => activeModal === modal;
    const closeModal = () => setActiveModal(null);

    // Show only jobs with the same team id or without any team id
    const searchJobs = useCallback(
        async (q?: string) => {
            setIsLoadingJobs(true);
            try {
                const res = await listJobs(q ? { ...defaultListJobsParams, query: q } : defaultListJobsParams);
                const filteredJobs = res.list.filter((job) => {
                    const hasTeam = Boolean(job.teamId);
                    const isSameTeam = job.teamId === manifest?.team?.id;
                    return !hasTeam || isSameTeam;
                });
                setJobsResults(filteredJobs);
            } finally {
                setIsLoadingJobs(false);
            }
        },
        [manifest?.team?.id]
    );

    const debouncedSearchJobs = useMemo(() => debounce((q: string) => searchJobs(q), 500), [searchJobs]);

    const jobsOptions: DefaultOptionType[] = useMemo(
        () => jobsResults.map((j) => ({ value: j.referenceId, label: j.referenceId, id: j.id })),
        [jobsResults]
    );

    const navigateBack = () => {
        navigate(-1);
    };

    const handleCreateJob = () => {
        navigate('./create-job');
    };

    const goToEnsCreation = (options?: { multipleEnsConfirmed?: boolean }) => {
        navigate(`/invoice-upload/${DeclarationCountry.EU}/${DeclarationInternalType.ENS}`, {
            state: { manifestId, multipleEnsConfirmed: options?.multipleEnsConfirmed },
        });
    };

    const handleCreateEns = () => {
        setShowActionsPopover(false);

        if (isAccompanied && (manifest?.jobs?.length ?? 0) > 1) {
            openModal('create-multiple-ens');
            return;
        }

        goToEnsCreation();
    };

    const handleAddExistingJob = async (jobId: string) => {
        try {
            if (!manifestId) {
                throw new Error('Missing manifest details');
            }

            await addJobToManifest({
                jobId,
                manifestId,
            });

            Notification({
                messageTitle: 'Success',
                description: 'Job added to manifest',
                type: 'success',
                placement: 'top',
            });

            closeModal();
            // Notify the overview tab that the jobs table needs to be updated
            window.EventBus.publish(Events.ADDED_EXISTING_JOB_TO_MANIFEST);
        } catch (error) {
            const errorMessage = error instanceof AxiosError ? error?.response?.data?.message : 'Something went wrong';

            Notification({
                messageTitle: 'Could not add job to manifest',
                description: errorMessage,
                type: 'error',
                placement: 'top',
            });
        } finally {
            setIsLoadingJobs(false);
        }
    };

    const handleRemoveExternalReference = async (ref: ExternalReference) => {
        if (!manifestId || !manifest) {
            return Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: 'Could not get manifest data. Please try again after reloading the page.',
            });
        }

        try {
            const payload: UpdateManifestExternalReferencesPayload = {
                ensReference: manifest.ensReference,
                pbn: manifest.pbn,
                gmr: manifest.gmr,
                [ref.referenceType]: null,
            };

            await updateManifestExternalReferences(manifestId!, payload);
            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Updated',
                description: 'External reference has been successfully updated.',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: errorMessage || 'Could not remove external reference.',
            });
        }
    };

    const handleAddExternalReference = async (externalReference: ExternalReference) => {
        if (!manifestId || !manifest)
            return Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: 'Could not get manifest data. Please try again after reloading the page.',
            });
        const payload: UpdateManifestExternalReferencesPayload = {
            ensReference: manifest.ensReference,
            pbn: manifest.pbn,
            gmr: manifest.gmr,
        };
        if (externalReference.referenceType === ReferenceTypes.MANIFEST_ENS_REFERENCE) {
            payload.ensReference = externalReference.referenceNumber;
        } else if (externalReference.referenceType === ReferenceTypes.PBN) {
            payload.pbn = externalReference.referenceNumber;
        } else if (externalReference.referenceType === ReferenceTypes.GMR) {
            payload.gmr = externalReference.referenceNumber;
        }
        try {
            await updateManifestExternalReferences(manifestId, payload);
            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Added',
                description: 'External reference has been successfully added to the manifest.',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: errorMessage || 'Could not add external reference to this manifest.',
            });
        }
    };

    const handleEditManifest = async () => {
        if (!manifestId) {
            return Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Operation Failed',
                description: 'Could not get manifest data. Please try again after reloading the page.',
            });
        }

        // We do not check for non-amendable declarations when manifest is DRAFT
        // as we're not actually sending amendments in such case
        if (isDraft) {
            openModal('edit-manifest');
            return;
        }

        try {
            const response = await getManifestDeclarations(manifestId);
            const allDeclarations = response.flatMap((job) => job.declarations);
            const hasAmendableDeclarations = allDeclarations.some((d) =>
                AMENDABLE_DECLARATION_STATUSES.includes(d.status)
            );
            const nonAmendableJobs = getJobsWithNonAmendableDeclarations(response);

            setDeclarationsState({ status: 'ready', hasAmendableDeclarations, nonAmendableJobs });

            if (nonAmendableJobs.length > 0) {
                openModal('non-amendable-declarations');
            } else {
                openModal('edit-manifest');
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage,
            });
        }
    };

    const refreshTransportDiscrepancies = useCallback(async () => {
        if (!manifestId) return;

        if (!manifest?.transportDetailsMismatch) {
            setJobsWithDiscrepacies([]);
            return;
        }

        setIsLoadingDiscrepancies(true);

        try {
            const res = await getManifestJobsWithTransportDetailsDiscrepancies(manifestId);
            setJobsWithDiscrepacies(res);
        } catch (err) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Transport Details Error',
                description: getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsLoadingDiscrepancies(false);
        }
    }, [manifestId, manifest?.transportDetailsMismatch]);

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Manifests', path: '../manifests' },
            { breadcrumbName: manifest?.referenceId ?? '', path: '' },
        ]);
    }, [manifest?.referenceId, manifestId, setBreadcrumbRoutes]);

    // Show transport details discrepancies warning
    useEffect(() => {
        refreshTransportDiscrepancies();
    }, [refreshTransportDiscrepancies]);

    useEffect(() => {
        setIsLoadingManifest(true);

        if (manifestId) {
            getManifestById(manifestId).finally(() => setIsLoadingManifest(false));
        }
    }, [manifestId, getManifestById]);

    useEffect(() => {
        if (!manifest?.id && isLoadingManifest) {
            showGlobalOverlay({ type: 'LoadingOverlay' });
        } else {
            hideGlobalOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingManifest, manifest?.id]);

    useEffect(() => {
        if (activeModal === 'add-existing-job') searchJobs();
    }, [activeModal, searchJobs]);

    useEffect(() => {
        if (!manifestId) return;

        const loadDeclarationsState = async () => {
            setDeclarationsState({ status: 'loading' });

            if (isCompleted) {
                setDeclarationsState({
                    status: 'disabled',
                    tooltip: 'Updating manifest in the current status is disabled.',
                });
                return;
            }

            if (isDraft) {
                setDeclarationsState({ status: 'ready', hasAmendableDeclarations: false, nonAmendableJobs: [] });
                return;
            }

            const response = await getManifestDeclarations(manifestId);
            const allDeclarations = response.flatMap((job) => job.declarations);
            const hasAmendableDeclarations = allDeclarations.some((d) =>
                AMENDABLE_DECLARATION_STATUSES.includes(d.status)
            );
            const hasEditableDeclarations = allDeclarations.some((d) =>
                EDITABLE_DECLARATION_STATUSES.includes(d.status)
            );

            if (!hasAmendableDeclarations && !hasEditableDeclarations) {
                setDeclarationsState({
                    status: 'disabled',
                    tooltip: 'No submitted declarations on this manifest to amend.',
                });
                return;
            }

            setDeclarationsState({
                status: 'ready',
                hasAmendableDeclarations,
                nonAmendableJobs: getJobsWithNonAmendableDeclarations(response),
            });
        };

        loadDeclarationsState().catch(() => setDeclarationsState({ status: 'error' }));
    }, [manifestId, isCompleted, isDraft]);

    const tabsItems = useMemo(
        () => [
            { key: ManifestTabKeys.OVERVIEW, label: 'Overview' },
            { key: ManifestTabKeys.ENS, label: 'ENS' },
            { key: ManifestTabKeys.DOCUMENTS, label: 'Documents' },
            { key: ManifestTabKeys.NOTES, label: 'Notes' },
            { key: ManifestTabKeys.HISTORY, label: 'History' },
        ],
        []
    );

    if (isLoadingManifest) return null;

    return (
        <StyledPageWrapper>
            <PageHeader
                title="Manifest Details"
                icon={null}
                showBackButton
                onBackButtonClick={navigateBack}
                additionalContent={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 32,
                            marginLeft: 32,
                        }}
                    >
                        <span>Manifest ID: {manifest?.referenceId}</span>
                        <ManifestStatus status={manifest?.status as ManifestStatusType} />
                        <span>Created: {formattedCreatedAt}</span>
                        <span>Team: {manifest?.team?.name ?? '-'}</span>
                    </div>
                }
                sidebar={
                    <Space size={8}>
                        {canCreateJobs && (
                            <Tooltip
                                title={isCompleted ? 'Updating manifest in the current status is disabled.' : null}
                            >
                                <span>
                                    <NewJobButtonPopover
                                        open={showNewJobPopup}
                                        setOpen={setShowNewJobPopup}
                                        onCreateJob={handleCreateJob}
                                        disabled={isCompleted}
                                        onAddExistingJob={() => {
                                            openModal('add-existing-job');
                                            setShowNewJobPopup(false);
                                        }}
                                        onAddThirdPartyJob={() => {
                                            openModal('add-third-party-job');
                                            setShowNewJobPopup(false);
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        )}

                        {(canUpdateManifests || canCreateDeclarations) && (
                            <ManifestActionsPopover
                                open={showActionsPopover}
                                onOpenChange={setShowActionsPopover}
                                onEditManifest={canUpdateManifests ? handleEditManifest : undefined}
                                editManifestDisabled={declarationsState.status !== 'ready'}
                                editManifestTooltip={editManifestTooltip}
                                onCreateEns={canCreateDeclarations ? handleCreateEns : undefined}
                            />
                        )}
                    </Space>
                }
            />

            <TransportDetailsDiscrepenciesWarning
                affectedJobs={jobsWithDiscrepancies}
                loading={isLoadingDiscrepancies}
            />

            <StyledTabs
                style={{
                    marginTop: 24,
                    position: 'relative',
                }}
                activeKey={activeTabKey}
                onChange={(k) => {
                    const activeKey = k as ManifestTabKey;
                    if (activeKey === ManifestTabKeys.OVERVIEW) {
                        navigate(`/manifests/${manifestId}`, {
                            state: { activeKey },
                            replace: true,
                        });
                    } else {
                        navigate(`/manifests/${manifestId}/${activeKey}`, {
                            replace: true,
                        });
                    }
                    setActiveTabKey(activeKey);
                }}
                items={tabsItems}
                tabBarExtraContent={
                    canUpdateManifestExternalReferences && (
                        <Button onClick={() => openModal('add-external-reference')}>Add External Reference</Button>
                    )
                }
            />
            {children}

            {canCreateJobs && (
                <AddJobModal
                    open={isModalOpen('add-existing-job')}
                    onCancel={closeModal}
                    onConfirm={handleAddExistingJob}
                    options={jobsOptions}
                    onSearch={debouncedSearchJobs}
                    loading={isLoadingJobs}
                />
            )}

            {manifest && (
                <ExternalReferencesModal
                    type="manifest"
                    open={isModalOpen('add-external-reference')}
                    onClose={closeModal}
                    externalReferences={getManifestExternalReferences(manifest)}
                    onUpdateExternalReferences={handleAddExternalReference}
                    onRemoveExternalReference={handleRemoveExternalReference}
                />
            )}

            {manifest && canUpdateManifests && (
                <EditManifestFormModal
                    manifest={manifest}
                    open={isModalOpen('edit-manifest')}
                    onSuccess={closeModal}
                    onClose={closeModal}
                    jobsWithNonAmendableDeclarations={
                        declarationsState.status === 'ready' ? declarationsState.nonAmendableJobs : []
                    }
                    requiresAmendment={
                        declarationsState.status === 'ready' && declarationsState.hasAmendableDeclarations
                    }
                />
            )}

            {manifest && canUpdateManifests && (
                <AddThirdPartyJobModal
                    open={isModalOpen('add-third-party-job')}
                    manifestId={manifest.id}
                    onClose={closeModal}
                />
            )}

            <CreateMultipleEnsModal
                open={isModalOpen('create-multiple-ens')}
                onClose={closeModal}
                onConfirm={() => {
                    closeModal();
                    goToEnsCreation({ multipleEnsConfirmed: true });
                }}
            />

            <NonAmendableDeclarationsModal
                open={isModalOpen('non-amendable-declarations')}
                onConfirm={() => openModal('edit-manifest')}
                onClose={closeModal}
                data={declarationsState.status === 'ready' ? declarationsState.nonAmendableJobs : []}
            />
        </StyledPageWrapper>
    );
};

export default ManifestDetails;
